import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, Loader2, Zap, Clock, Play, ToggleLeft, ToggleRight, Workflow, Trash2,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface WorkflowItem {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  triggerType?: string;
  trigger?: { type?: string } | any;
  lastRunAt?: string;
  runCount: number;
  createdAt: string;
}

const TRIGGER_COLORS: Record<string, string> = {
  manual: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-blue-50 text-blue-700',
  webhook: 'bg-purple-50 text-purple-700',
  event: 'bg-orange-50 text-orange-700',
  record_created: 'bg-green-50 text-green-700',
  record_updated: 'bg-yellow-50 text-yellow-700',
};

export function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const [showModal, setShowModal] = useState(false);
  const [showCreated, setShowCreated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '' });

  const fetchWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      const res = await api.get('/workflows', { params });
      setWorkflows(res.data.data?.workflows ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/workflows', {
        name: form.name,
        description: form.description || undefined,
      });
      setForm({ name: '', description: '' });
      setShowModal(false);
      setShowCreated(true);
      fetchWorkflows();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      setToggling(id);
      await api.patch(`/workflows/${id}/toggle`);
      setWorkflows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, isActive: !w.isActive } : w))
      );
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle workflow');
    } finally {
      setToggling(null);
    }
  };

  const filtered = workflows.filter((w) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return w.name.toLowerCase().includes(q) || (w.description || '').toLowerCase().includes(q);
  });

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflows</h1>
          <p className="text-sm text-gray-500">Automate your business processes</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Workflow
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search workflows..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading workflows...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchWorkflows} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Workflow size={40} className="mb-2" />
          <p className="text-sm">No workflows found</p>
          <p className="text-xs">Create your first workflow to automate tasks</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((wf) => (
            <div
              key={wf.id}
              className="rounded-xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-md"
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="truncate text-sm font-semibold text-gray-900">{wf.name}</h3>
                  {wf.description && (
                    <p className="mt-1 text-xs text-gray-500 line-clamp-2">{wf.description}</p>
                  )}
                </div>
                <button
                  onClick={() => handleToggle(wf.id)}
                  disabled={toggling === wf.id}
                  className="ml-3 flex-shrink-0"
                  title={wf.isActive ? 'Deactivate' : 'Activate'}
                >
                  {toggling === wf.id ? (
                    <Loader2 size={20} className="animate-spin text-gray-400" />
                  ) : wf.isActive ? (
                    <ToggleRight size={24} className="text-green-500" />
                  ) : (
                    <ToggleLeft size={24} className="text-gray-300" />
                  )}
                </button>
              </div>

              <div className="mb-3">
                {(() => {
                  const triggerType = wf.triggerType || wf.trigger?.type || 'manual';
                  return (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${TRIGGER_COLORS[triggerType] || 'bg-gray-100 text-gray-600'}`}
                    >
                      <Zap size={10} />
                      {triggerType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </span>
                  );
                })()}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Play size={10} />
                    {wf.runCount} runs
                  </span>
                  {wf.lastRunAt && (
                    <span className="flex items-center gap-1">
                      <Clock size={10} />
                      Last: {formatDate(wf.lastRunAt)}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(`Delete workflow "${wf.name}"?`)) {
                      api.delete(`/workflows/${wf.id}`).then(() => fetchWorkflows()).catch(() => alert('Failed to delete'));
                    }
                  }}
                  className="rounded-md p-1 text-gray-300 hover:bg-red-50 hover:text-red-600 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 mt-4 rounded-lg border border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* Create Workflow Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Create Workflow</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Workflow Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Welcome Email Sequence"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Describe what this workflow does..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.name}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Builder Placeholder Modal */}
      {showCreated && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreated(false)}>
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-8 text-center shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--saffron-50)]">
              <Zap size={28} className="text-[var(--ink-0)]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Workflow Created!</h3>
            <p className="mt-2 text-sm text-gray-500">
              Visual workflow builder coming soon. You will be able to design complex automation flows with a drag-and-drop interface.
            </p>
            <button
              onClick={() => setShowCreated(false)}
              className="mt-6 rounded-lg bg-[var(--ink-0)] px-6 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
